import { BackgroundJobScheduleStatus, BackgroundJobScheduleStatusLabels, type InputData, URLKeys } from 'noddi-async';
import { filterUnitFor, useFilterUnits } from 'noddi-ui-web';
import { entriesToOptions } from 'noddi-util';

import { CREATED_AT_FILTER_TEXT } from '../Date/useCreatedAtFilter';
import { backgroundJobFacetFilter } from './backgroundJobFacetFilter';
import { useBackgroundJobFilterOptions } from './useBackgroundJobFilterOptions';

const SCHEDULE_STATUS_OPTIONS = entriesToOptions(BackgroundJobScheduleStatus, BackgroundJobScheduleStatusLabels);

type BackgroundJobScheduleFilterInput = Omit<
  InputData<typeof URLKeys.getPaginatedBackgroundJobSchedules>,
  'pageIndex' | 'pageSize' | 'order' | 'ordering' | 'search'
>;

const scheduleFilterUnit = filterUnitFor<BackgroundJobScheduleFilterInput>();

const statusFilter = scheduleFilterUnit.multiSelect('statusIn', {
  title: 'Status',
  description: 'Filter by schedule lifecycle status',
  options: SCHEDULE_STATUS_OPTIONS,
  exclude: true
});

export const useScheduledJobsTableFilters = ({
  adapterRelated = false
}: {
  /** Scopes the list to adapter-related tasks. */
  adapterRelated?: boolean;
} = {}) => {
  const { taskNameOptions, load, withSelected } = useBackgroundJobFilterOptions();
  const task = backgroundJobFacetFilter({
    key: 'taskNameIn',
    title: 'Task',
    description: 'Filter by one or more task names',
    options: taskNameOptions,
    load,
    withSelected
  });

  const filters = useFilterUnits([
    ...(adapterRelated ? [scheduleFilterUnit.locked({ adapterRelated: true })] : []),
    scheduleFilterUnit.compose('taskNameIn', task.urlParams, (values, setValues) => {
      const { control, include, exclude } = task.resolve(values, setValues);
      return { controls: control, params: { taskNameIn: include, excludedTaskNameIn: exclude } };
    }),
    statusFilter,
    scheduleFilterUnit.dateRange('runAt', {
      title: 'Run at',
      description: 'Filter by scheduled run time',
      exclude: true
    }),
    scheduleFilterUnit.dateRange('createdAt', {
      ...CREATED_AT_FILTER_TEXT,
      description: 'Filter by when the schedule was created',
      exclude: true
    }),
    scheduleFilterUnit.dateRange('dispatchedAt', {
      title: 'Dispatched at',
      description: 'Filter by when the schedule was dispatched to the job queue',
      exclude: true
    })
  ]);
  const statusControl = filters.controlOf(statusFilter);

  return {
    filters,
    /** Scopes the list to these statuses (a "Pending only" shortcut), clearing status exclusions. */
    setStatusIn: (values: BackgroundJobScheduleStatus[]) => {
      statusControl?.onChange({ include: values, exclude: [] });
    }
  };
};
